import { css, html, nothing } from 'lit';
// Registers <d3-dialog-shell>, the viewer below.
import '@dice-driven/d3-kit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { shortFingerprint } from '../../lib/fingerprint.js';
import { formatMediumDate } from '../../lib/format.js';
import {
    MSA_AWAITING_COUNTERSIGNATURE,
    MSA_AWAITING_SIGNATURE,
    MSA_EXECUTED,
    MSA_RECORDED,
} from '../../lib/msa.js';
import { documentFileName, downloadDocument } from '../../lib/print-document.js';
import { marked } from '../../vendor/marked.js';
import { agreementDetails } from './print-agreement.js';
import './tax-form-notice.js';

/**
 * Everything a freelancer has agreed to, as a list with View (the text in a
 * dialog) and Download for each:
 * their master services agreement (the signed PDF once both parties have
 * signed; the text they signed before that), then the platform documents they
 * accepted (terms, privacy notice, notice designation), each as a standalone
 * file of its text. The tax form they owe, while it isn't on file, sits above
 * the list. The markdown downloaded here is our own document text.
 */
class AgreementsPage extends BaseComponent {
    static properties = {
        _agreements: { type: Array, state: true },
        _msa: { type: Object, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        // The document open in the viewer: { title, meta, html, download }.
        _viewing: { type: Object, state: true },
    };

    static styles = css`
        :host { display: block; }
        h4 { margin: 0 0 1rem; font-size: 1.5rem; font-weight: 600; }
        tax-form-notice { margin-bottom: 1rem; }
        .list { border: 1px solid #e5e5e5; border-radius: 8px; background: #fff; }
        .row-item {
            display: flex; justify-content: space-between; align-items: center; gap: 1rem;
            padding: 0.75rem 1.25rem;
        }
        .row-item + .row-item { border-top: 1px solid #eee; }
        .title { font-weight: 600; }
        .meta { color: #666; font-size: 0.85rem; }
        .actions { display: flex; gap: 0.5rem; flex-shrink: 0; }
        .viewer-meta { color: #666; font-size: 0.85rem; margin-bottom: 0.75rem; }
        .viewer-body { font-size: 0.95rem; line-height: 1.6; }
        .viewer-body h1 { font-size: 1.3rem; margin-top: 0; }
        .viewer-body h2 { font-size: 1.1rem; margin-top: 1.25rem; }
        .viewer-body table { width: 100%; border-collapse: collapse; margin: 0.75rem 0; }
        .viewer-body th, .viewer-body td { border: 1px solid #dee2e6; padding: 0.35rem 0.5rem; vertical-align: top; }
    `;

    constructor() {
        super();
        this._agreements = [];
        this._msa = null;
        this._loading = true;
        this._error = null;
        this._viewing = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        try {
            const [data, msa] = await Promise.all([
                api.get('/api/agreements'),
                api.get('/api/msa'),
            ]);
            this._agreements = data.agreements || [];
            this._msa = msa;
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _downloadMSA() {
        this._error = null;
        const msa = this._msa;
        try {
            if (msa.has_document) {
                const { url } = await api.get('/api/msa/download');
                window.open(url, '_blank', 'noopener');
                return;
            }
            // Signed but not yet countersigned: there is no PDF yet, so it is
            // the text they signed, with their signature's details.
            const signing = msa.signing;
            downloadDocument(
                {
                    title: 'Master Services Agreement',
                    details: [
                        `Signed by ${signing.contractor.typed_signature} on ${new Date(signing.contractor.signed_at).toUTCString()}`,
                        'Awaiting countersignature by Dice Driven Design LLC',
                        `Document fingerprint: ${shortFingerprint(signing.document_sha256)}`,
                    ],
                    bodyHTML: marked.parse(signing.rendered_text),
                },
                documentFileName('Master Services Agreement', msa.document.version),
            );
        } catch (err) {
            this._error = err.message;
        }
    }

    _downloadAgreement(a) {
        downloadDocument(
            { title: a.title, details: agreementDetails(a), bodyHTML: marked.parse(a.body || '') },
            documentFileName(a.title, a.version),
        );
    }

    // The text of the MSA they signed, while there is one to show: a
    // recorded MSA (signed outside the app) has only its PDF.
    _viewMSA() {
        const signing = this._msa.signing;
        this._viewing = {
            title: 'Master Services Agreement',
            meta: `${this._msaMeta()} · Document fingerprint ${shortFingerprint(signing.document_sha256)}`,
            html: marked.parse(signing.rendered_text),
            download: () => this._downloadMSA(),
        };
    }

    _viewAgreement(a) {
        this._viewing = {
            title: a.title,
            meta: agreementDetails(a).join(' · '),
            html: marked.parse(a.body || ''),
            download: () => this._downloadAgreement(a),
        };
    }

    _renderViewer() {
        const v = this._viewing;
        return html`
            <d3-dialog-shell
                ?open="${!!v}"
                heading="${v?.title ?? ''}"
                primary-label="Download"
                cancel-label="Close"
                light-dismiss
                @d3-dialog-shell:cancel="${() => (this._viewing = null)}"
                @d3-dialog-shell:submit="${() => v?.download()}"
            >
                ${
                    v
                        ? html`<div class="viewer-meta">${v.meta}</div>
                              <div class="viewer-body" .innerHTML="${v.html}"></div>`
                        : nothing
                }
            </d3-dialog-shell>
        `;
    }

    _msaMeta() {
        const msa = this._msa;
        switch (msa?.status) {
            case MSA_EXECUTED:
                return `Signed by you and Dice Driven Design LLC · in effect since ${formatMediumDate(msa.signing.company.signed_at)}`;
            case MSA_AWAITING_COUNTERSIGNATURE:
                return `Signed by you ${formatMediumDate(msa.signing.contractor.signed_at)} · awaiting Dice Driven's countersignature`;
            case MSA_RECORDED:
                return 'Signed outside the app · on file';
            case MSA_AWAITING_SIGNATURE:
                return 'Not signed yet';
            default:
                return 'There is no agreement for you yet';
        }
    }

    _renderMSARow() {
        const status = this._msa?.status;
        const downloadable =
            status === MSA_AWAITING_COUNTERSIGNATURE ||
            ((status === MSA_EXECUTED || status === MSA_RECORDED) && this._msa.has_document);
        const viewable = !!this._msa?.signing?.rendered_text;
        return html`
            <div class="row-item">
                <div>
                    <div class="title">Master Services Agreement</div>
                    <div class="meta">${this._msaMeta()}</div>
                </div>
                <span class="actions">
                    ${
                        viewable
                            ? html`<button class="btn btn-outline-secondary btn-sm" @click="${this._viewMSA}">
                                  <i class="bi bi-eye"></i> View
                              </button>`
                            : nothing
                    }
                    ${
                        downloadable
                            ? html`<button class="btn btn-outline-secondary btn-sm" @click="${this._downloadMSA}">
                                  <i class="bi bi-download"></i> Download
                              </button>`
                            : nothing
                    }
                </span>
            </div>
        `;
    }

    render() {
        if (this._loading) {
            return html`<div class="spinner-border spinner-border-sm text-primary" role="status"></div>`;
        }
        return html`
            <h4>Agreements</h4>
            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            <tax-form-notice .taxForm="${this._msa?.tax_form}"></tax-form-notice>
            <div class="list">
                ${this._renderMSARow()}
                ${this._agreements.map(
                    (a) => html`
                    <div class="row-item">
                        <div>
                            <div class="title">${a.title}</div>
                            <div class="meta">
                                Version ${a.version}${a.accepted && a.accepted_at ? ` · accepted ${formatMediumDate(a.accepted_at)}` : ' · not accepted'}
                            </div>
                        </div>
                        <span class="actions">
                            <button class="btn btn-outline-secondary btn-sm" @click="${() => this._viewAgreement(a)}">
                                <i class="bi bi-eye"></i> View
                            </button>
                            <button class="btn btn-outline-secondary btn-sm" @click="${() => this._downloadAgreement(a)}">
                                <i class="bi bi-download"></i> Download
                            </button>
                        </span>
                    </div>
                `,
                )}
            </div>
            ${this._renderViewer()}
        `;
    }
}

customElements.define('agreements-page', AgreementsPage);
